import { useEffect, useState, useCallback } from 'react'
import { useRouter } from 'next/router'
import {
  parseSessionStorage,
  validateEnrollmentData,
  clearSessionStorage,
} from './utils'
import { createEnrollment } from './api'

/**
 * Hook to load enrollment data from sessionStorage
 * @returns {Object} { enrollmentData, flowType, error, setError }
 */
export function useEnrollmentData() {
  const [enrollmentData, setEnrollmentData] = useState(null)
  const [flowType, setFlowType] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Check for claim enrollment data first
    const claimData = parseSessionStorage('pendingClaimEnrollment')
    if (claimData) {
      setEnrollmentData(claimData)
      setFlowType('claim')
      return
    }

    // Check for pending link tuple
    const linkData = parseSessionStorage('pendingLinkTuple')
    if (linkData) {
      setEnrollmentData(linkData)
      setFlowType('link')
      return
    }

    // No data found
    if (!claimData && !completeData && !linkData) {
      setError(
        'No pending enrollment found. You may have already completed enrollment.'
      )
    }
  }, [])

  return { enrollmentData, flowType, error, setError }
}

/**
 * Hook to handle link enrollment flow
 * @param {Object} params
 * @param {string} params.flowType - Flow type
 * @param {Object} params.enrollmentData - Enrollment data
 * @param {Object} params.user - Auth0 user object
 * @param {boolean} params.isAuthenticated - Authentication status
 * @param {boolean} params.authLoading - Auth loading state
 * @returns {Object} { isLinking, error, enrollmentSubmitted, setError }
 */
export function useLinkEnrollment({
  flowType,
  enrollmentData,
  user,
  isAuthenticated,
  authLoading,
}) {
  const router = useRouter()
  const [isLinking, setIsLinking] = useState(false)
  const [error, setError] = useState(null)
  const [enrollmentSubmitted, setEnrollmentSubmitted] = useState(false)

  // Auto-process pendingLinkTuple flow
  useEffect(() => {
    if (flowType !== 'link') return

    if (!authLoading && !isAuthenticated) {
      return
    }

    if (authLoading || !isAuthenticated || !user || !enrollmentData) {
      return
    }

    if (isLinking || enrollmentSubmitted || error) {
      return
    }

    const processEnrollment = async () => {
      setIsLinking(true)
      setError(null)

      try {
        const validation = validateEnrollmentData(enrollmentData, 'link')
        if (!validation.isValid) {
          throw new Error(validation.error)
        }

        const queuePayload = {
          first_name: enrollmentData.first_name,
          last_name: enrollmentData.last_name,
          account_address: enrollmentData.street_address_1,
          email: enrollmentData.email,
          phone_number: enrollmentData.phone_number || '',
          account_id: enrollmentData.account_id,
          auth0_id: user.sub,
        }

        await createEnrollment(queuePayload)
        clearSessionStorage('pendingLinkTuple')
        setEnrollmentSubmitted(true)
        setIsLinking(false)
        router.push('/success')
      } catch (err) {
        console.error('Failed to queue enrollment:', err)
        setError(err.message)
        setIsLinking(false)
        setEnrollmentSubmitted(true)
      }
    }

    processEnrollment()
  }, [
    authLoading,
    isAuthenticated,
    user,
    enrollmentData,
    flowType,
    isLinking,
    enrollmentSubmitted,
    router,
  ])

  return {
    isLinking,
    error,
    enrollmentSubmitted,
    setError,
    setIsLinking,
  }
}
